import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building, 
  User, 
  Mail, 
  Phone,
  FileText,
  Search,
  Building2,
  Users,
  LogOut,
  Edit,
  Save,
  ExternalLink,
  X,
  Copy,
  Crown,
  CreditCard,
  Trash2,
  Settings,
  Lock,
  Key,
  Eye,
  EyeOff,
  AlertTriangle
} from "lucide-react";
import BanksManager from "../components/admin/BanksManager";
import BanksManagerV2 from "../components/admin/BanksManagerV2";
import BankSheetsManager from "../components/admin/BankSheetsManager";
import DocumentTextsEditor from "../components/admin/DocumentTextsEditor";
import SubAdminManager from "../components/admin/SubAdminManager";
import Logo from "../components/common/Logo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [bankModalBroker, setBankModalBroker] = useState(null);
  const [allBanks, setAllBanks] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [savingBanks, setSavingBanks] = useState(false);
  const [validationChecks, setValidationChecks] = useState({
    companyNameVerified: false,
    cuiVerified: false,
    caenVerified: false,
    anpcVerified: false,
    userSearched: false,
    criminalRecordChecked: false
  });
  const [rejectionReason, setRejectionReason] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editedBroker, setEditedBroker] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  const isSubAdmin = user?.role === "subadmin";
  
  // State-uri pentru gestionarea parolelor și email
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [adminPasswordData, setAdminPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [brokerNewPassword, setBrokerNewPassword] = useState("");
  const [brokerNewEmail, setBrokerNewEmail] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showBrokerPassword, setShowBrokerPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [keysfinLoading, setKeysfinLoading] = useState(false);
  const [keysfinResult, setKeysfinResult] = useState(null);
  const [showKeysfinModal, setShowKeysfinModal] = useState(false);

  useEffect(() => {
    // Verifică dacă utilizatorul este admin sau subadmin
    if (!user || (user.role !== "admin" && user.role !== "subadmin")) {
      navigate("/login");
      return;
    }
    // SubAdmin-ii nu pot vedea intermediarii
    if (!isSubAdmin) {
      fetchBrokers();
    }
    fetchAllBanks();
  }, [user, navigate, filter, isSubAdmin]);

  const fetchBrokers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/admin/brokers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: filter }
      });
      setBrokers(response.data.brokers);
    } catch (error) {
      console.error("Eroare la preluarea brokerilor:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBanks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/products/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllBanks(response.data.banks || []);
    } catch (error) {
      console.error("Eroare la preluarea băncilor:", error);
    }
  };

  const openBankModal = async (broker) => {
    setBankModalBroker(broker);
    try {
      const token = localStorage.getItem("token");
      const brokerId = broker.id || broker.slug;
      const response = await axios.get(`${API_URL}/api/admin/brokers/${brokerId}/allowed-banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBanks(response.data.allowedBanks || []);
    } catch (error) {
      console.error("Eroare la preluarea băncilor permise:", error);
      setSelectedBanks([]);
    }
  };

  const closeBankModal = () => {
    setBankModalBroker(null);
    setSelectedBanks([]);
  };

  const toggleBank = (bankId) => {
    setSelectedBanks(prev => 
      prev.includes(bankId) 
        ? prev.filter(id => id !== bankId)
        : [...prev, bankId]
    );
  };

  const selectAllBanks = () => {
    setSelectedBanks(allBanks.map(b => b.id));
  };

  const deselectAllBanks = () => {
    setSelectedBanks([]);
  };

  const saveAllowedBanks = async () => {
    if (!bankModalBroker) return;
    
    setSavingBanks(true);
    try {
      const token = localStorage.getItem("token");
      const brokerId = bankModalBroker.id || bankModalBroker.slug;
      await axios.put(
        `${API_URL}/api/admin/brokers/${brokerId}/allowed-banks`,
        { allowedBanks: selectedBanks },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      closeBankModal();
      fetchBrokers();
    } catch (error) {
      console.error("Eroare la salvarea băncilor:", error);
    } finally {
      setSavingBanks(false);
    }
  };

  const openValidationModal = (broker) => {
    setSelectedBroker(broker);
    setEditedBroker({
      companyName: broker.companyData?.companyName || broker.companyName || "",
      cui: broker.companyData?.cui || broker.cui || "",
      address: broker.companyData?.address || broker.address || "",
      jNumber: broker.companyData?.jNumber || broker.jNumber || "",
      codCAEN: broker.companyData?.codCAEN || broker.codCAEN || "",
      bankAccount: broker.companyData?.bankAccount || broker.bankAccount || "",
      bankName: broker.companyData?.bankName || broker.bankName || "",
      firstName: broker.representativeData?.firstName || (broker.representativeName?.split(" ")[0]) || "",
      lastName: broker.representativeData?.lastName || (broker.representativeName?.split(" ").slice(1).join(" ")) || "",
      email: broker.representativeData?.email || broker.email || "",
      phone: broker.representativeData?.phone || broker.phone || "",
      cnp: broker.representativeData?.cnp || broker.representativeCnp || "",
      idSeries: broker.representativeData?.idSeries || broker.representativeIdSeries || "",
      idNumber: broker.representativeData?.idNumber || broker.representativeIdNumber || "",
      website: broker.website || ""
    });
    setEditMode(false);
    setValidationChecks({
      companyNameVerified: false,
      cuiVerified: false,
      caenVerified: false,
      anpcVerified: false,
      userSearched: false,
      criminalRecordChecked: false
    });
    setRejectionReason("");
  };

  const closeValidationModal = () => {
    setSelectedBroker(null);
    setEditedBroker(null);
    setEditMode(false);
    setValidationChecks({
      companyNameVerified: false,
      cuiVerified: false,
      caenVerified: false,
      anpcVerified: false,
      userSearched: false,
      criminalRecordChecked: false
    });
    setRejectionReason("");
  };

  const handleEditChange = (field, value) => {
    setEditedBroker(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveChanges = async () => {
    if (!editedBroker || !selectedBroker) return;
    
    setSavingChanges(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/admin/brokers/${selectedBroker.id}`,
        editedBroker,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Modificările au fost salvate cu succes!");
      setEditMode(false);
      fetchBrokers();
      // Actualizează și selectedBroker cu noile date
      setSelectedBroker(prev => ({
        ...prev,
        companyData: {
          companyName: editedBroker.companyName,
          cui: editedBroker.cui,
          address: editedBroker.address,
          jNumber: editedBroker.jNumber,
          bankAccount: editedBroker.bankAccount,
          bankName: editedBroker.bankName
        },
        representativeData: {
          firstName: editedBroker.firstName,
          lastName: editedBroker.lastName,
          email: editedBroker.email,
          phone: editedBroker.phone,
          cnp: editedBroker.cnp,
          idSeries: editedBroker.idSeries,
          idNumber: editedBroker.idNumber
        },
        website: editedBroker.website
      }));
    } catch (error) {
      console.error("Eroare la salvarea modificărilor:", error);
      alert("Eroare la salvarea modificărilor. Vă rugăm să încercați din nou.");
    } finally {
      setSavingChanges(false);
    }
  };

  const handleSubscriptionChange = async (brokerId, newPlan) => {
    setSavingSubscription(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/admin/brokers/${brokerId}/subscription`,
        { plan: newPlan, status: "active" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const planNames = {
        basic: "BASIC (Gratuit)",
        professional: "PROFESIONAL",
        premium: "PREMIUM"
      };
      alert(`Planul de abonament a fost schimbat la ${planNames[newPlan]}.`);
      fetchBrokers();
      
      // Actualizează și în modal dacă e deschis
      if (selectedBroker && selectedBroker.id === brokerId) {
        setSelectedBroker(prev => ({
          ...prev,
          subscription: { ...prev.subscription, plan: newPlan, status: "active" }
        }));
      }
    } catch (error) {
      console.error("Eroare la schimbarea abonamentului:", error);
      alert(error.response?.data?.detail || "Eroare la schimbarea abonamentului.");
    } finally {
      setSavingSubscription(false);
    }
  };

  const getSubscriptionBadge = (subscription) => {
    const plan = subscription?.plan || "basic";
    const status = subscription?.status || "active";
    
    if (status !== "active") {
      return <Badge className="bg-gray-500 text-white">Inactiv</Badge>;
    }
    
    switch (plan) {
      case "professional":
        return <Badge className="bg-blue-500 text-white"><Crown className="h-3 w-3 mr-1" />PRO</Badge>;
      case "premium":
        return <Badge className="bg-purple-500 text-white"><Crown className="h-3 w-3 mr-1" />PREMIUM</Badge>;
      default:
        return <Badge className="bg-gray-400 text-white">BASIC</Badge>;
    }
  };

  const handleApprove = async () => {
    // Verifică că toate checkbox-urile sunt bifate
    const allChecked = Object.values(validationChecks).every(check => check === true);
    if (!allChecked) {
      alert("Vă rugăm să confirmați toate verificările înainte de aprobare!");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/admin/brokers/${selectedBroker.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Intermediar aprobat cu succes! Email de notificare a fost trimis.");
      closeValidationModal();
      fetchBrokers();
    } catch (error) {
      console.error("Eroare la aprobare:", error);
      alert("Eroare la aprobarea intermediarului. Vă rugăm să încercați din nou.");
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert("Vă rugăm să introduceți un motiv pentru respingere!");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      // Folosește id sau slug
      const brokerId = selectedBroker.id || selectedBroker.slug;
      await axios.post(
        `${API_URL}/api/admin/brokers/${brokerId}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Intermediar respins. Email de notificare a fost trimis.");
      closeValidationModal();
      fetchBrokers();
    } catch (error) {
      console.error("Eroare la respingere:", error);
      alert("Eroare la respingerea intermediarului. Vă rugăm să încercați din nou.");
    }
  };

  const handleDelete = async (broker) => {
    const brokerName = broker.companyData?.companyName || broker.slug || "acest intermediar";
    if (!window.confirm(`Sigur doriți să ștergeți permanent "${brokerName}"? Această acțiune este ireversibilă!`)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      // Folosește id sau slug
      const brokerId = broker.id || broker.slug;
      await axios.delete(
        `${API_URL}/api/admin/brokers/${brokerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Intermediar șters cu succes.");
      closeValidationModal();
      fetchBrokers();
    } catch (error) {
      console.error("Eroare la ștergere:", error);
      alert("Eroare la ștergerea intermediarului. Vă rugăm să încercați din nou.");
    }
  };

  // Funcții pentru schimbarea parolei admin
  const handleAdminPasswordChange = async () => {
    if (adminPasswordData.newPassword !== adminPasswordData.confirmPassword) {
      alert("Parolele noi nu se potrivesc!");
      return;
    }
    if (adminPasswordData.newPassword.length < 6) {
      alert("Parola nouă trebuie să aibă cel puțin 6 caractere!");
      return;
    }
    
    setSavingPassword(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/auth/password/change`,
        {
          current_password: adminPasswordData.currentPassword,
          new_password: adminPasswordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Parola a fost schimbată cu succes!");
      setShowAdminPasswordModal(false);
      setAdminPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      console.error("Eroare la schimbarea parolei:", error);
      alert(error.response?.data?.detail || "Eroare la schimbarea parolei. Verificați parola curentă.");
    } finally {
      setSavingPassword(false);
    }
  };

  // Funcție pentru resetarea parolei unui intermediar
  const handleResetBrokerPassword = async () => {
    if (brokerNewPassword.length < 6) {
      alert("Parola nouă trebuie să aibă cel puțin 6 caractere!");
      return;
    }
    
    setSavingPassword(true);
    try {
      const token = localStorage.getItem("token");
      const brokerId = selectedBroker.id || selectedBroker.slug;
      await axios.post(
        `${API_URL}/api/admin/users/${brokerId}/reset-password`,
        {
          user_id: brokerId,
          new_password: brokerNewPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Parola intermediarului a fost resetată cu succes!");
      setShowResetPasswordModal(false);
      setBrokerNewPassword("");
    } catch (error) {
      console.error("Eroare la resetarea parolei:", error);
      alert(error.response?.data?.detail || "Eroare la resetarea parolei intermediarului.");
    } finally {
      setSavingPassword(false);
    }
  };

  // Funcție pentru modificarea email-ului unui intermediar
  const handleUpdateBrokerEmail = async () => {
    if (!brokerNewEmail || !brokerNewEmail.includes("@")) {
      alert("Vă rugăm să introduceți o adresă de email validă!");
      return;
    }
    
    setSavingEmail(true);
    try {
      const token = localStorage.getItem("token");
      const brokerId = selectedBroker.id || selectedBroker.slug;
      await axios.put(
        `${API_URL}/api/admin/users/${brokerId}/email`,
        {
          user_id: brokerId,
          new_email: brokerNewEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Email-ul intermediarului a fost actualizat cu succes!");
      setShowEditEmailModal(false);
      setBrokerNewEmail("");
      // Actualizează local
      if (editedBroker) {
        setEditedBroker({ ...editedBroker, email: brokerNewEmail });
      }
      fetchBrokers();
    } catch (error) {
      console.error("Eroare la actualizarea email-ului:", error);
      alert(error.response?.data?.detail || "Eroare la actualizarea email-ului intermediarului.");
    } finally {
      setSavingEmail(false);
    }
  };

  // Funcție pentru verificare KeysFin
  const handleKeysfinVerification = async (cui) => {
    if (!cui) {
      alert("CUI lipsă pentru verificare");
      return;
    }
    
    setKeysfinLoading(true);
    setKeysfinResult(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/auth/verify-cui/${cui}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setKeysfinResult(response.data);
      // Nu mai deschidem modal, rezultatul apare inline
    } catch (error) {
      console.error("Eroare verificare CUI:", error);
      setKeysfinResult({
        success: false,
        message: error.response?.data?.detail || "Eroare la verificare"
      });
    } finally {
      setKeysfinLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-500 text-white"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "approved":
        return <Badge className="bg-green-500 text-white"><CheckCircle className="h-3 w-3 mr-1" />Aprobat</Badge>;
      case "rejected":
        return <Badge className="bg-red-500 text-white"><XCircle className="h-3 w-3 mr-1" />Respins</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const [adminTab, setAdminTab] = useState(isSubAdmin ? "banks" : "brokers");

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <div>
                <h1 className="text-xl font-bold text-gray-900">FINBRO</h1>
                <p className="text-sm text-gray-500">
                  {isSubAdmin ? "Panel Sub-Administrator" : "Panel Administrare"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium text-gray-900">
                  {isSubAdmin ? "Sub-Administrator" : "Administrator"}
                </p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} title="Deconectare">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-6">
          <CardContent className="p-6">
            <Tabs value={adminTab} onValueChange={setAdminTab}>
              <TabsList className={`grid w-full ${isSubAdmin ? 'grid-cols-2' : 'grid-cols-5'} mb-6`}>
                {!isSubAdmin && (
                  <TabsTrigger value="brokers" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Gestionare Intermediari
                  </TabsTrigger>
                )}
                <TabsTrigger value="banks" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Administrare Bănci
                </TabsTrigger>
                {!isSubAdmin && (
                  <TabsTrigger value="sheets" className="flex items-center gap-2" data-testid="admin-sheets-tab">
                    <FileText className="h-4 w-4" />
                    Fișe Bănci (BRINX)
                  </TabsTrigger>
                )}
                {!isSubAdmin && (
                  <TabsTrigger value="doctexts" className="flex items-center gap-2" data-testid="admin-doctexts-tab">
                    <FileText className="h-4 w-4" />
                    Texte Documente
                  </TabsTrigger>
                )}
                <TabsTrigger value="settings" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Setări Cont
                </TabsTrigger>
              </TabsList>
              
              {/* Brokers Tab - doar pentru admin principal */}
              {!isSubAdmin && (
              <TabsContent value="brokers">
                {/* Filtre */}
                <div className="flex gap-4 mb-6">
                  <Button
                    variant={filter === "pending" ? "default" : "outline"}
                    onClick={() => setFilter("pending")}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Pending
                  </Button>
                  <Button
                    variant={filter === "approved" ? "default" : "outline"}
                    onClick={() => setFilter("approved")}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Aprobați
                  </Button>
                  <Button
                    variant={filter === "rejected" ? "default" : "outline"}
                    onClick={() => setFilter("rejected")}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Respinși
                  </Button>
                </div>

                {/* Lista de intermediari */}
                {loading ? (
                  <div className="text-center py-8">Se încarcă...</div>
                ) : brokers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nu există intermediari cu statusul &quot;{filter}&quot;
                  </div>
                ) : (
                  <div className="space-y-4">
                    {brokers.map((broker) => (
                      <Card key={broker.id} className="border border-gray-200">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                            <Building className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-semibold">{broker.companyData?.companyName}</h3>
                            {getStatusBadge(broker.status)}
                            {broker.status === "approved" && getSubscriptionBadge(broker.subscription)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-600">CUI:</span>
                              <span className="font-medium">{broker.companyData?.cui}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-600">Reprezentant:</span>
                              <span className="font-medium">
                                {broker.representativeData?.firstName} {broker.representativeData?.lastName}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-600">Email:</span>
                              <span className="font-medium">{broker.representativeData?.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-600">Telefon:</span>
                              <span className="font-medium">{broker.representativeData?.phone}</span>
                            </div>
                          </div>

                          <div className="mt-3 text-xs text-gray-500">
                            Înregistrat la: {new Date(broker.createdAt).toLocaleString("ro-RO")}
                          </div>
                        </div>

                        {(broker.status === "pending" || broker.status === "pending_validation") && (
                          <Button
                            onClick={() => openValidationModal(broker)}
                            className="ml-4"
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Validează
                          </Button>
                        )}
                        
                        {broker.status === "approved" && (
                          <div className="flex flex-col gap-2 ml-4">
                            <Button
                              onClick={() => openValidationModal(broker)}
                              variant="outline"
                              size="sm"
                              className="border-purple-300 text-purple-700 hover:bg-purple-100"
                            >
                              <Key className="h-4 w-4 mr-2" />
                              Gestionare Cont
                            </Button>
                            <Button
                              onClick={() => openBankModal(broker)}
                              variant="outline"
                              size="sm"
                            >
                              <Building2 className="h-4 w-4 mr-2" />
                              Bănci ({broker.allowedBanks?.length || 'Toate'})
                            </Button>
                            <Select
                              value={broker.subscription?.plan || "basic"}
                              onValueChange={(value) => handleSubscriptionChange(broker.id || broker.slug, value)}
                              disabled={savingSubscription}
                            >
                              <SelectTrigger className="w-[160px]">
                                <CreditCard className="h-4 w-4 mr-2" />
                                <SelectValue placeholder="Plan" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="basic">BASIC (Gratuit)</SelectItem>
                                <SelectItem value="professional">PROFESIONAL</SelectItem>
                                <SelectItem value="premium">PREMIUM</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
              </TabsContent>
              )}
              
              {/* Banks Tab */}
              <TabsContent value="banks">
                <BanksManagerV2 />
              </TabsContent>

              {/* Bank Sheets Tab (BRINX knowledge base) */}
              {!isSubAdmin && (
              <TabsContent value="sheets">
                <BankSheetsManager />
              </TabsContent>
              )}

              {/* Document Texts Tab */}
              {!isSubAdmin && (
              <TabsContent value="doctexts">
                <DocumentTextsEditor />
              </TabsContent>
              )}

              {/* Settings Tab */}
              <TabsContent value="settings">
                <div className="space-y-6">
                  {/* Secțiune Schimbare Parolă */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Lock className="h-5 w-5 text-blue-600" />
                        Schimbă Parola
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        Modificați parola contului dvs. de administrator
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="max-w-md space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Parola curentă</Label>
                          <div className="relative mt-1">
                            <Input
                              type={showCurrentPassword ? "text" : "password"}
                              value={adminPasswordData.currentPassword}
                              onChange={(e) => setAdminPasswordData({...adminPasswordData, currentPassword: e.target.value})}
                              placeholder="Introduceți parola curentă"
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            >
                              {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Parola nouă</Label>
                          <div className="relative mt-1">
                            <Input
                              type={showNewPassword ? "text" : "password"}
                              value={adminPasswordData.newPassword}
                              onChange={(e) => setAdminPasswordData({...adminPasswordData, newPassword: e.target.value})}
                              placeholder="Introduceți parola nouă (min. 6 caractere)"
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Confirmă parola nouă</Label>
                          <div className="relative mt-1">
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              value={adminPasswordData.confirmPassword}
                              onChange={(e) => setAdminPasswordData({...adminPasswordData, confirmPassword: e.target.value})}
                              placeholder="Reintroduceți parola nouă"
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <Button 
                          onClick={handleAdminPasswordChange}
                          disabled={savingPassword || !adminPasswordData.currentPassword || !adminPasswordData.newPassword || !adminPasswordData.confirmPassword}
                          className="w-full"
                        >
                          {savingPassword ? "Se salvează..." : "Schimbă Parola"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Info despre contul admin */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <User className="h-5 w-5 text-green-600" />
                        Informații Cont
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <p><strong>Email:</strong> {user?.email}</p>
                        <p><strong>Rol:</strong> {isSubAdmin ? "Sub-Administrator" : "Administrator Principal"}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Secțiune Sub-Administratori - doar pentru admin principal */}
        {!isSubAdmin && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Sub-Administratori
              </CardTitle>
              <p className="text-sm text-gray-500">
                Sub-administratorii pot gestiona doar băncile și produsele, nu și intermediarii
              </p>
            </CardHeader>
            <CardContent>
              <SubAdminManager />
            </CardContent>
          </Card>
        )}
      </main>

      {/* Modal de validare */}
      {selectedBroker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <CardTitle>Validare Intermediar: {editedBroker?.companyName || selectedBroker.companyData?.companyName}</CardTitle>
                <div className="flex gap-2">
                  {!editMode ? (
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => setEditMode(true)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editează
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={handleSaveChanges}
                        disabled={savingChanges}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {savingChanges ? "Se salvează..." : "Salvează"}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditMode(false)}
                        className="text-white hover:bg-white/20"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Informații intermediar */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Informații Companie</h4>
                    {editedBroker?.cui && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleKeysfinVerification(editedBroker.cui)}
                          disabled={keysfinLoading}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-wait transition-colors shadow-sm"
                        >
                          <Search className="h-3.5 w-3.5" />
                          {keysfinLoading ? "Se verifică..." : "Verifică CUI"}
                        </button>
                        <a 
                          href={`https://www.listafirme.ro/search.asp?q=${editedBroker.cui}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors shadow-sm"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          ListaFirme.ro
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {editMode ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Denumire Firmă</Label>
                        <Input
                          value={editedBroker?.companyName || ""}
                          onChange={(e) => handleEditChange("companyName", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">CUI</Label>
                        <Input
                          value={editedBroker?.cui || ""}
                          onChange={(e) => handleEditChange("cui", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs text-gray-500">Adresă Sediu</Label>
                        <Input
                          value={editedBroker?.address || ""}
                          onChange={(e) => handleEditChange("address", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Nr. Reg. Comerț</Label>
                        <Input
                          value={editedBroker?.jNumber || ""}
                          onChange={(e) => handleEditChange("jNumber", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Cod CAEN</Label>
                        <Input
                          value={editedBroker?.codCAEN || ""}
                          onChange={(e) => handleEditChange("codCAEN", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Cont Bancar</Label>
                        <Input
                          value={editedBroker?.bankAccount || ""}
                          onChange={(e) => handleEditChange("bankAccount", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Bancă</Label>
                        <Input
                          value={editedBroker?.bankName || ""}
                          onChange={(e) => handleEditChange("bankName", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Website</Label>
                        <Input
                          value={editedBroker?.website || ""}
                          onChange={(e) => handleEditChange("website", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <p><strong>Companie:</strong> {editedBroker?.companyName}</p>
                        <p className="flex items-center gap-2">
                          <strong>CUI:</strong> {editedBroker?.cui}
                          <button 
                            onClick={() => navigator.clipboard.writeText(editedBroker?.cui || "")}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copiază CUI"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </p>
                        <p className="md:col-span-2"><strong>Adresă:</strong> {editedBroker?.address}</p>
                        <p><strong>Nr. Reg. Com.:</strong> {editedBroker?.jNumber}</p>
                        <p className="flex items-center gap-2">
                          <strong>Cod CAEN:</strong> 
                          <span className={['6619', '6622', '6492', '6499'].includes(editedBroker?.codCAEN) ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                            {editedBroker?.codCAEN || '-'}
                          </span>
                          {editedBroker?.codCAEN && (
                            ['6619', '6622', '6492', '6499'].includes(editedBroker?.codCAEN) 
                              ? <span className="text-green-600 text-xs">✓ Valid</span>
                              : <span className="text-orange-600 text-xs">⚠️ Verifică</span>
                          )}
                        </p>
                        <p><strong>Cont:</strong> {editedBroker?.bankAccount}</p>
                        <p><strong>Bancă:</strong> {editedBroker?.bankName}</p>
                        <p><strong>Website:</strong> {editedBroker?.website || "-"}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rezultat Verificare CUI - apare inline sub datele companiei */}
                {keysfinResult && (
                  <div className={`p-4 rounded-lg border-2 ${keysfinResult.success ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        {keysfinResult.success ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                            <span className="text-emerald-800">Date Verificate ({keysfinResult.source})</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-5 w-5 text-red-600" />
                            <span className="text-red-800">Verificare Eșuată</span>
                          </>
                        )}
                      </h4>
                      <button
                        onClick={() => setKeysfinResult(null)}
                        className="text-gray-500 hover:text-gray-700"
                        title="Închide"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {keysfinResult.success ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Denumire:</span>
                          <span className={`font-medium ${keysfinResult.data?.denumire === editedBroker?.companyName ? 'text-emerald-700' : 'text-orange-600'}`}>
                            {keysfinResult.data?.denumire || "-"}
                            {keysfinResult.data?.denumire !== editedBroker?.companyName && (
                              <span className="ml-1 text-xs">⚠️ diferit</span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">CUI:</span>
                          <span className="font-medium text-emerald-700">{keysfinResult.data?.cui || "-"}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Nr. Reg. Com.:</span>
                          <span className={`font-medium ${keysfinResult.data?.nrRegCom === editedBroker?.jNumber ? 'text-emerald-700' : 'text-orange-600'}`}>
                            {keysfinResult.data?.nrRegCom || "-"}
                            {keysfinResult.data?.nrRegCom !== editedBroker?.jNumber && editedBroker?.jNumber && (
                              <span className="ml-1 text-xs">⚠️ diferit</span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Cod CAEN:</span>
                          <span className={`font-medium ${keysfinResult.data?.codCAEN === editedBroker?.codCAEN ? 'text-emerald-700' : 'text-orange-600'}`}>
                            {keysfinResult.data?.codCAEN || "-"}
                            {keysfinResult.data?.codCAEN !== editedBroker?.codCAEN && editedBroker?.codCAEN && (
                              <span className="ml-1 text-xs">⚠️ diferit</span>
                            )}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Stare:</span>
                          <span className={`font-medium ${keysfinResult.data?.stare === 'activ' ? 'text-emerald-700' : 'text-red-600'}`}>
                            {keysfinResult.data?.stare?.toUpperCase() || "-"}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Înființare:</span>
                          <span className="font-medium">{keysfinResult.data?.dataInregistrare || "-"}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-span-2">
                          <span className="text-gray-500 min-w-[100px]">Adresă:</span>
                          <span className="font-medium">{keysfinResult.data?.adresa || "-"}</span>
                        </div>
                        {keysfinResult.data?.telefon && (
                          <div className="flex items-start gap-2">
                            <span className="text-gray-500 min-w-[100px]">Telefon:</span>
                            <span className="font-medium">{keysfinResult.data.telefon}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-red-600">{keysfinResult.message}</p>
                    )}
                  </div>
                )}

                {/* Informații reprezentant */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-3">Informații Reprezentant</h4>
                  
                  {editMode ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Prenume</Label>
                        <Input
                          value={editedBroker?.firstName || ""}
                          onChange={(e) => handleEditChange("firstName", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Nume</Label>
                        <Input
                          value={editedBroker?.lastName || ""}
                          onChange={(e) => handleEditChange("lastName", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Email</Label>
                        <Input
                          type="email"
                          value={editedBroker?.email || ""}
                          onChange={(e) => handleEditChange("email", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Telefon</Label>
                        <Input
                          value={editedBroker?.phone || ""}
                          onChange={(e) => handleEditChange("phone", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">CNP</Label>
                        <Input
                          value={editedBroker?.cnp || ""}
                          onChange={(e) => handleEditChange("cnp", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="w-1/3">
                          <Label className="text-xs text-gray-500">Serie CI</Label>
                          <Input
                            value={editedBroker?.idSeries || ""}
                            onChange={(e) => handleEditChange("idSeries", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Număr CI</Label>
                          <Input
                            value={editedBroker?.idNumber || ""}
                            onChange={(e) => handleEditChange("idNumber", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <p><strong>Nume:</strong> {editedBroker?.firstName} {editedBroker?.lastName}</p>
                        <p><strong>CNP:</strong> {editedBroker?.cnp}</p>
                        <p className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-blue-500" />
                          <strong>Email:</strong> 
                          <a href={`mailto:${editedBroker?.email}`} className="text-blue-600 hover:underline">
                            {editedBroker?.email}
                          </a>
                          <button 
                            onClick={() => navigator.clipboard.writeText(editedBroker?.email || "")}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copiază Email"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </p>
                        <p className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-green-500" />
                          <strong>Telefon:</strong> 
                          <a href={`tel:${editedBroker?.phone}`} className="text-blue-600 hover:underline">
                            {editedBroker?.phone}
                          </a>
                          <button 
                            onClick={() => navigator.clipboard.writeText(editedBroker?.phone || "")}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copiază Telefon"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </p>
                        <p><strong>Serie/Nr. CI:</strong> {editedBroker?.idSeries} {editedBroker?.idNumber}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Acțiuni Cont Intermediar - doar pentru userii aprobați */}
                {selectedBroker?.status === "approved" && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold mb-3 text-purple-800 flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Gestionare Cont Intermediar
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBrokerNewPassword("");
                          setShowResetPasswordModal(true);
                        }}
                        className="border-purple-300 text-purple-700 hover:bg-purple-100"
                      >
                        <Lock className="h-4 w-4 mr-2" />
                        Resetează Parola
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBrokerNewEmail(editedBroker?.email || "");
                          setShowEditEmailModal(true);
                        }}
                        className="border-purple-300 text-purple-700 hover:bg-purple-100"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Modifică Email
                      </Button>
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      Utilizați aceste opțiuni când intermediarul uită parola sau email-ul.
                    </p>
                  </div>
                )}

                {/* Link-uri verificare rapidă */}
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold mb-3 text-yellow-800">Link-uri Verificare Rapidă</h4>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`https://www.anaf.ro/verificare/?cui=${editedBroker?.cui}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Verifică ANAF
                    </a>
                    <a
                      href="https://www.anpc.gov.ro/registrul-icf/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Registru ANPC ICF
                    </a>
                    <a
                      href={`https://www.google.com/search?q="${editedBroker?.companyName}"+${editedBroker?.cui}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors"
                    >
                      <Search className="h-3 w-3" />
                      Caută pe Google
                    </a>
                    <a
                      href={`https://www.google.com/search?q="${editedBroker?.firstName}+${editedBroker?.lastName}"`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors"
                    >
                      <User className="h-3 w-3" />
                      Caută Reprezentant
                    </a>
                  </div>
                </div>

                {/* Checklist validare */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">Checklist Validare</h4>
                  <p className="text-sm text-gray-600">
                    Vă rugăm să efectuați toate verificările necesare și să bifați fiecare pas finalizat:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id="companyName"
                        checked={validationChecks.companyNameVerified}
                        onCheckedChange={(checked) =>
                          setValidationChecks({ ...validationChecks, companyNameVerified: checked })
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor="companyName" className="font-medium cursor-pointer">
                          Verificare Nume Firmă
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Am verificat că numele companiei corespunde cu cel din documentele oficiale
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id="cui"
                        checked={validationChecks.cuiVerified}
                        onCheckedChange={(checked) =>
                          setValidationChecks({ ...validationChecks, cuiVerified: checked })
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor="cui" className="font-medium cursor-pointer">
                          Verificare CUI
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Am verificat CUI-ul pe site-ul ANAF și este valid și activ
                        </p>
                      </div>
                    </div>

                    <div className={`flex items-start space-x-3 p-3 border rounded-lg ${
                      editedBroker?.codCAEN && ['6619', '6622', '6492', '6499'].includes(editedBroker?.codCAEN) 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-orange-50 border-orange-300'
                    }`}>
                      <Checkbox
                        id="caen"
                        checked={validationChecks.caenVerified}
                        onCheckedChange={(checked) =>
                          setValidationChecks({ ...validationChecks, caenVerified: checked })
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor="caen" className="font-medium cursor-pointer flex items-center gap-2">
                          Verificare Cod CAEN
                          {editedBroker?.codCAEN && (
                            ['6619', '6622', '6492', '6499'].includes(editedBroker?.codCAEN)
                              ? <span className="text-green-600 text-xs font-normal">✓ Cod acceptat ({editedBroker?.codCAEN})</span>
                              : <span className="text-orange-600 text-xs font-normal">⚠️ Cod neobișnuit ({editedBroker?.codCAEN})</span>
                          )}
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Am verificat că firma are cod CAEN acceptat pentru intermediari de credite (6619, 6622, 6492, 6499)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id="anpc"
                        checked={validationChecks.anpcVerified}
                        onCheckedChange={(checked) =>
                          setValidationChecks({ ...validationChecks, anpcVerified: checked })
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor="anpc" className="font-medium cursor-pointer">
                          Verificare Listă ANPC Intermediar Avizat
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Am verificat că firma apare în registrul ANPC de intermediari de credite avizați
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id="userSearch"
                        checked={validationChecks.userSearched}
                        onCheckedChange={(checked) =>
                          setValidationChecks({ ...validationChecks, userSearched: checked })
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor="userSearch" className="font-medium cursor-pointer">
                          Căutare Utilizator pe Internet
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Am căutat reprezentantul pe internet și nu am găsit informații negative
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id="criminal"
                        checked={validationChecks.criminalRecordChecked}
                        onCheckedChange={(checked) =>
                          setValidationChecks({ ...validationChecks, criminalRecordChecked: checked })
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor="criminal" className="font-medium cursor-pointer">
                          Verificare Cazier
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Am solicitat și verificat cazierul judiciar al reprezentantului
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Motiv respingere (opțional) */}
                <div className="space-y-2">
                  <Label>Motiv Respingere (opțional)</Label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Introduceți motivul respingerii dacă doriți să respingeți acest intermediar..."
                    rows={4}
                  />
                </div>

                {/* Butoane */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={handleApprove}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!Object.values(validationChecks).every(check => check === true)}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Aprobă Intermediar
                  </Button>
                  <Button
                    onClick={handleReject}
                    variant="destructive"
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Respinge
                  </Button>
                  <Button
                    onClick={() => handleDelete(selectedBroker)}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Șterge
                  </Button>
                  <Button
                    onClick={closeValidationModal}
                    variant="outline"
                  >
                    Anulează
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal pentru selectarea băncilor */}
      {bankModalBroker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
              <CardTitle className="text-xl">
                Bănci Permise - {bankModalBroker.companyData?.companyName}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Selectați băncile pe care acest intermediar le poate vedea și utiliza.
                  {selectedBanks.length === 0 && " (Dacă nu selectați nimic, va vedea toate băncile)"}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllBanks}>
                    Selectează toate
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAllBanks}>
                    Deselectează toate
                  </Button>
                </div>
              </div>
              
              <div className="border rounded-lg max-h-[40vh] overflow-y-auto">
                {allBanks.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    Nu există bănci în sistem. Adăugați bănci din tab-ul &quot;Administrare Bănci&quot;.
                  </div>
                ) : (
                  <div className="divide-y">
                    {allBanks.map((bank) => (
                      <div
                        key={bank.id}
                        className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 ${
                          selectedBanks.includes(bank.id) ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                        onClick={() => toggleBank(bank.id)}
                      >
                        <Checkbox
                          checked={selectedBanks.includes(bank.id)}
                          className="pointer-events-none"
                        />
                        <Building2 className="h-5 w-5 text-blue-600" />
                        <div className="flex-1">
                          <span className="font-medium">{bank.name}</span>
                          {bank.productsCount > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              ({bank.productsCount} produse)
                            </span>
                          )}
                        </div>
                        {selectedBanks.includes(bank.id) ? (
                          <Badge className="bg-green-500 text-white">Selectată</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">Neselectată</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <strong>Selectate:</strong> {selectedBanks.length} din {allBanks.length} bănci
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  onClick={closeBankModal}
                  variant="outline"
                >
                  Anulează
                </Button>
                <Button
                  onClick={saveAllowedBanks}
                  disabled={savingBanks}
                >
                  {savingBanks ? "Se salvează..." : "Salvează"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal pentru resetarea parolei intermediarului */}
      {showResetPasswordModal && selectedBroker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Resetare Parolă Intermediar
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Resetați parola pentru: <strong>{editedBroker?.email}</strong>
                </p>
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Intermediarul va trebui să folosească noua parolă pentru a se autentifica.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Parola nouă</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showBrokerPassword ? "text" : "password"}
                      value={brokerNewPassword}
                      onChange={(e) => setBrokerNewPassword(e.target.value)}
                      placeholder="Introduceți parola nouă (min. 6 caractere)"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowBrokerPassword(!showBrokerPassword)}
                    >
                      {showBrokerPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setBrokerNewPassword("");
                    }}
                    className="flex-1"
                  >
                    Anulează
                  </Button>
                  <Button
                    onClick={handleResetBrokerPassword}
                    disabled={savingPassword || brokerNewPassword.length < 6}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {savingPassword ? "Se salvează..." : "Resetează Parola"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal pentru modificarea email-ului intermediarului */}
      {showEditEmailModal && selectedBroker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Modificare Email Intermediar
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Modificați adresa de email pentru: <strong>{editedBroker?.firstName} {editedBroker?.lastName}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Email curent: {editedBroker?.email}
                </p>
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Intermediarul va trebui să folosească noul email pentru a se autentifica.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Email nou</Label>
                  <Input
                    type="email"
                    value={brokerNewEmail}
                    onChange={(e) => setBrokerNewEmail(e.target.value)}
                    placeholder="Introduceți noul email"
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditEmailModal(false);
                      setBrokerNewEmail("");
                    }}
                    className="flex-1"
                  >
                    Anulează
                  </Button>
                  <Button
                    onClick={handleUpdateBrokerEmail}
                    disabled={savingEmail || !brokerNewEmail || !brokerNewEmail.includes("@")}
                    className="flex-1"
                  >
                    {savingEmail ? "Se salvează..." : "Salvează Email"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
